import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Field } from '../../interfaces/Field';
import { POSMultiselectDropdown } from '@publicisgroupe/p-os-components';
import { Entity } from '../../interfaces/Entity';
import { capitalize } from '../../utils/stringUtils';
import useGetXdmEntityDataQuery from '../../hooks/queries/useGetXdmEntityDataQuery';
import {
  ALL_OPTION_VALUE,
  getXdmEntityNameProperty,
  mapFromXdmEntity,
  getEntityProperty,
} from '../../utils/xdmUtils';
import { XdmEntityObject } from '../../models';

type Props = {
  fieldInfo: Field;
  value: Entity[];
  onChange: (newValue: Entity[]) => void;
  filter?: ((xdmEntity: XdmEntityObject) => boolean) | null;
  dataTestId?: string;
  errorMessage?: string;
};

export const XdmMultiselectDropdown: FC<Props> = ({
  fieldInfo,
  value,
  onChange,
  filter,
  dataTestId,
  errorMessage,
}) => {
  if (!fieldInfo.xdm) {
    throw new Error(
      'XdmMultiselectDropdown cannot be rendered for non xdm field'
    );
  }

  const { xdm: xdmInfo } = fieldInfo;
  const { entityName } = xdmInfo;
  const { data: xdmData, isLoading: xdmDataLoading } =
    useGetXdmEntityDataQuery(entityName);

  const filteredXdmData = useMemo(() => {
    if (xdmDataLoading) {
      return undefined;
    }

    if (filter) {
      return (xdmData || []).filter(filter);
    }

    return xdmData || [];
  }, [xdmDataLoading, filter, xdmData]);

  const nameProperty = useMemo(() => {
    return getXdmEntityNameProperty(xdmInfo.mappings);
  }, [xdmInfo]);

  const idProperty = useMemo(() => {
    return xdmInfo.key;
  }, [xdmInfo]);

  const entityIdProperty = useMemo(() => {
    return getEntityProperty(xdmInfo.mappings, idProperty);
  }, [xdmInfo, idProperty]);

  const entityNameProperty = useMemo(() => {
    return getEntityProperty(xdmInfo.mappings, nameProperty);
  }, [xdmInfo, nameProperty]);

  useEffect(() => {
    if (!filteredXdmData) {
      return;
    }

    const xdmEntityKeyPropName = fieldInfo.xdm?.key;
    if (!xdmEntityKeyPropName) {
      throw new Error('entity key is not defined');
    }

    const selectedValuesNotAvailable = value.filter(
      (i) => !filteredXdmData.find((x) => x[idProperty] === i[entityIdProperty])
    );

    if (
      selectedValuesNotAvailable.length === 1 &&
      selectedValuesNotAvailable[0][entityIdProperty] === '*'
    ) {
      return;
    }

    if (selectedValuesNotAvailable.length > 0) {
      onChange(
        value.filter(
          (i) =>
            !selectedValuesNotAvailable.find(
              (x) => x[entityIdProperty] === i[entityIdProperty]
            )
        )
      );
    }
  }, [
    fieldInfo,
    filteredXdmData,
    idProperty,
    entityIdProperty,
    value,
    onChange,
  ]);

  const isOptionAlreadySelected = useCallback(
    (optionValue: string) => {
      return !!value.find((doc) => doc[entityIdProperty] === optionValue);
    },
    [value, entityIdProperty]
  );

  const options = useMemo<
    { label: string; value: string; checked: boolean }[]
  >(() => {
    if (xdmDataLoading) {
      return [];
    }

    const multiselectOptions = filteredXdmData?.map((item) => {
      const isChecked = isOptionAlreadySelected(item[idProperty]);

      return {
        label: item[nameProperty],
        value: item[idProperty],
        checked: isChecked,
      };
    });

    const allOption = {
      label: 'Any (*)',
      value: ALL_OPTION_VALUE,
      checked: isOptionAlreadySelected(ALL_OPTION_VALUE),
    };

    if (allOption.checked) {
      multiselectOptions?.forEach((i) => {
        i.checked = true;
      });
    }

    return multiselectOptions?.length
      ? [allOption, ...(multiselectOptions || [])]
      : [];
  }, [
    filteredXdmData,
    xdmDataLoading,
    idProperty,
    nameProperty,
    isOptionAlreadySelected,
  ]);

  const handleAllOptionSelected = useCallback(() => {
    if (isOptionAlreadySelected(ALL_OPTION_VALUE)) {
      onChange([]);
      return;
    }

    onChange([
      {
        [entityIdProperty]: ALL_OPTION_VALUE,
        [entityNameProperty]: ALL_OPTION_VALUE,
      },
    ]);
  }, [entityIdProperty, entityNameProperty, isOptionAlreadySelected, onChange]);

  const getXdmEntityById = useCallback(
    (id: string) => {
      return filteredXdmData?.find((i) => i[idProperty].toString() === id);
    },
    [filteredXdmData, idProperty]
  );

  const onSelectHandler = useCallback(
    (selectedValue: string) => {
      if (!filteredXdmData) {
        return;
      }

      if (selectedValue === ALL_OPTION_VALUE) {
        handleAllOptionSelected();
        return;
      }

      const selectedXdmEntity = getXdmEntityById(selectedValue.toString());
      if (!selectedXdmEntity) {
        throw new Error(`Xdm Entity not found! id ${selectedValue}`);
      }

      if (isOptionAlreadySelected(ALL_OPTION_VALUE)) {
        const newValues = options
          .filter(
            (i) =>
              i.value !== ALL_OPTION_VALUE &&
              i.value != selectedXdmEntity[idProperty]
          )
          .map((i) =>
            mapFromXdmEntity(
              xdmInfo.mappings,
              filteredXdmData.find(
                (xdmObject) => xdmObject[idProperty] === i.value
              ) || {}
            )
          );

        onChange(newValues);
        return;
      }

      const selectedEntity = mapFromXdmEntity(
        xdmInfo.mappings,
        selectedXdmEntity
      );
      if (isOptionAlreadySelected(selectedEntity[entityIdProperty])) {
        onChange([
          ...value.filter(
            (i) => i[entityIdProperty] !== selectedEntity[entityIdProperty]
          ),
        ]);

        return;
      }

      onChange([...value, selectedEntity]);
    },
    [
      filteredXdmData,
      idProperty,
      xdmInfo.mappings,
      options,
      value,
      entityIdProperty,
      isOptionAlreadySelected,
      onChange,
      handleAllOptionSelected,
      getXdmEntityById,
    ]
  );

  return (
    <POSMultiselectDropdown
      required={fieldInfo.required}
      error={errorMessage}
      label={capitalize(fieldInfo.name)}
      options={options}
      onSelect={onSelectHandler as any}
      dataTestId={dataTestId}
    />
  );
};
